import { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { PostMedia } from '../../../types/publishing';
import { activeSlideIndex, carouselAspectRatio } from '../../../lib/publishing/instagramCarousel';

// Instagram feed carousel preview (round 2 item 2). Every rule is in
// src/lib/publishing/instagramCarousel.ts; this only draws it.
//
// ⚠️ ONE SHARED RATIO — the first item's, measured when it loads, clamped to
// Instagram's 4:5..1.91:1 range. Every slide is cropped to it (object-cover),
// because that is what publishes; a slide keeping its own ratio would preview a
// post that cannot exist.
//
// Horizontal scroll-snap track (swipeable on touch), dot indicators that are
// real buttons jumping to their slide, and arrows on hover at desktop width.
// `onActiveIndexChange` exposes the slide in view — Instagram photo tagging
// addresses tags by media index and will hang on it.

export function InstagramCarousel({
  media,
  onActiveIndexChange,
}: {
  media: PostMedia[];
  onActiveIndexChange?: (index: number) => void;
}) {
  const track = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [ratio, setRatio] = useState<number>(carouselAspectRatio(null, null));

  const setActiveIndex = (i: number) => {
    setActive(i);
    onActiveIndexChange?.(i);
  };
  const onScroll = () => {
    const el = track.current;
    if (!el) return;
    const i = activeSlideIndex(el.scrollLeft, el.clientWidth, media.length);
    if (i !== active) setActiveIndex(i);
  };
  const goTo = (i: number) => {
    const el = track.current;
    const clamped = Math.max(0, Math.min(media.length - 1, i));
    // scrollTo is missing on elements in jsdom and some older browsers.
    if (el && typeof el.scrollTo === 'function') {
      el.scrollTo({ left: clamped * el.clientWidth, behavior: 'smooth' });
    }
    setActiveIndex(clamped);
  };

  return (
    <div data-testid="ig-carousel" className="group relative">
      <div
        ref={track}
        onScroll={onScroll}
        className="flex w-full snap-x snap-mandatory overflow-x-auto bg-black [scrollbar-width:none]"
        style={{ aspectRatio: String(ratio) }}
      >
        {media.map((m, i) => (
          <div key={`${m.url}-${i}`} className="h-full w-full shrink-0 snap-center">
            {m.type === 'video' ? (
              <video
                src={m.url}
                poster={m.thumbnailUrl ?? undefined}
                muted
                playsInline
                className="h-full w-full object-cover"
                onLoadedMetadata={
                  i === 0
                    ? (e) => setRatio(carouselAspectRatio(e.currentTarget.videoWidth, e.currentTarget.videoHeight))
                    : undefined
                }
              />
            ) : (
              <img
                src={m.url}
                alt={m.altText || m.filename || `slide ${i + 1}`}
                loading={i === 0 ? 'eager' : 'lazy'}
                className="h-full w-full object-cover"
                onLoad={
                  i === 0
                    ? (e) => setRatio(carouselAspectRatio(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight))
                    : undefined
                }
              />
            )}
          </div>
        ))}
      </div>
      <span className="absolute right-2 top-2 rounded-full bg-black/60 px-2 py-0.5 text-[11px] text-white">
        {active + 1}/{media.length}
      </span>
      {active > 0 && (
        <button
          type="button"
          aria-label="Previous slide"
          onClick={() => goTo(active - 1)}
          className="absolute left-2 top-1/2 hidden h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-slate-700 shadow md:group-hover:flex"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      )}
      {active < media.length - 1 && (
        <button
          type="button"
          aria-label="Next slide"
          onClick={() => goTo(active + 1)}
          className="absolute right-2 top-1/2 hidden h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-slate-700 shadow md:group-hover:flex"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      )}
      <div className="flex justify-center gap-1 py-2">
        {media.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`Go to slide ${i + 1}`}
            aria-current={i === active ? 'true' : undefined}
            onClick={() => goTo(i)}
            className={`h-1.5 w-1.5 rounded-full ${i === active ? 'bg-blue-500' : 'bg-slate-300'}`}
          />
        ))}
      </div>
    </div>
  );
}
